import React from 'react';
import { Button } from '../ui/button';
import { Trophy, TrendingUp } from 'lucide-react';
import { calculatePercentile } from '../../utils/formatArcaNumbers';

export interface RankingRowV2Props {
	icon: React.ReactNode;
	title: string;
	subtitle: string;
	position: number;
	total?: number;
	percentile?: number;
	amountToNext: number;
	isTop?: boolean;
	onAction?: () => void;
	translate?: (key: string) => string;
}

const RankingRowV2: React.FC<RankingRowV2Props> = ({
	icon,
	title,
	subtitle,
	position,
	total,
	percentile,
	amountToNext,
	isTop,
	onAction,
	translate
}) => {
	const pct = percentile ?? (total ? calculatePercentile(position, total) : 0);

	return (
		<div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 rounded-xl bg-white border border-gray-200 hover:border-primary/30 transition-colors">
			{/* Icon + Text */}
			<div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
				<div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
					{icon}
				</div>
				<div className="flex-1 min-w-0">
					<p className="font-semibold text-sm truncate">{title}</p>
					<p className="text-xs text-gray-500 truncate">{subtitle}</p>
				</div>
			</div>

			{/* Position + Progress + Action */}
			<div className="flex items-center justify-between sm:justify-end gap-4 pl-13 sm:pl-0">
				<div className="text-left sm:text-right shrink-0">
					<div className="flex items-baseline gap-1">
						<span className="text-lg sm:text-xl font-bold">#{position}</span>
						<span className="text-xs text-gray-500">/{total}</span>
					</div>
					<div className="w-16 sm:w-20 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
						<div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
					</div>
				</div>
				<div className="shrink-0">
					{isTop ? (
						<div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-secondary/10 flex items-center justify-center">
							<Trophy className="w-4 h-4 text-secondary" />
						</div>
					) : amountToNext > 0 ? (
						<Button
							size="sm"
							variant="outline"
							className="rounded-full border-primary/30 text-primary hover:bg-primary/5 text-xs h-auto py-1 sm:py-1.5 px-2 sm:px-3 flex flex-col items-center gap-0"
							onClick={(e) => {
								e.stopPropagation();
								onAction?.();
							}}
						>
							<span className="flex items-center gap-1 font-semibold">
								<TrendingUp className="w-3 h-3" />+{Math.round(amountToNext)}€/mes
							</span>
							<span className="text-[9px] sm:text-[10px] text-gray-500 font-normal hidden sm:block">
								{translate?.('benefit.rankings.ctaLabel') || 'Sube de puesto'}
							</span>
						</Button>
					) : (
						<div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/10 flex items-center justify-center">
							<Trophy className="w-4 h-4 text-primary" />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default RankingRowV2;
